import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import "./App.css";
import { useTranslation } from "react-i18next";
import "./i18n";
import config from "./config";

import Header from './components/Header';

import LanguageSelector from './components/common/selectors/LanguageSelector';
import CoverSection from "./components/common/sections/CoverSection";
import { ScrollToTopOrElement } from "./components/common/scroll/ScrollToTopOrElement";
import BookingForm from "./components/booking/BookingForm";
import BookingOverviewForm from "./components/booking/BookingOverviewForm";
import VoucherOverviewModal from "./components/booking/VoucherOverviewModal";

import Footer from './components/Footer';
import ContactForm from "./components/contact/ContactForm";
import ServicesList from "./components/service/ServicesList";
import RatesList from "./components/rate/RatesList";

import Home from "./pages/Home";
import Faq from "./pages/Faq";
import History from "./pages/History";

const globalCover = config.globalCover;

const Bookings = ({t}) => {
  const title = t("pages.bookings.title", { returnObjects: true });
  const location = useLocation();
  const [bookingInfo, setBookingInfo] = useState(null);
  const [voucherInfo, setVoucherInfo] = useState(null);

  useEffect(() => {
    const decodeBase64 = (encoded) => {
      try {
        const decoded = atob(encoded);
        const separatorIndex = decoded.indexOf('_');
        if (separatorIndex === -1) return null;
        const code = decoded.substring(0, separatorIndex);
        const email = decoded.substring(separatorIndex + 1);
        return { code, email };
      } catch (error) {
        return null;
      }
    };

    const queryParams = new URLSearchParams(location.search);

    const encryptedParam = queryParams.get('overview');
    if (encryptedParam) {
      const decryptedData = decodeBase64(encryptedParam);

      if (decryptedData) {
        setBookingInfo(decryptedData);
      }
    }

    const encryptedVoucherParam = queryParams.get('voucherOverview');
    if (encryptedVoucherParam) {
      const decryptedData = decodeBase64(encryptedVoucherParam);

      if (decryptedData) {
        setVoucherInfo(decryptedData);
      }
    }
  }, [location.search]);

  return (
    <div className="content">
      <CoverSection title={title} backgroundImage={globalCover} />
      <BookingForm t={t} />
      <div className="generic-separator" />
      <BookingOverviewForm t={t} bookingInfo={bookingInfo}/>
      {voucherInfo && <VoucherOverviewModal t={t} voucherInfo={voucherInfo} onClose={() => {setVoucherInfo('')}}/>}
    </div>
  );
}


const Rates = ({t}) => {
  const title = t("pages.rates.title", { returnObjects: true });
  return (
    <div className="content">
      <CoverSection title={title} backgroundImage={globalCover} />
      <RatesList t={t}/>
    </div>
  );
}

const Services = ({t}) => {
  const title = t("pages.services.title", { returnObjects: true });
  return (
    <div className="content">
      <CoverSection title={title} backgroundImage={globalCover} />
      <ServicesList t={t}/>
    </div>
  );
}

const Contact = ({t}) => {
  const title = t("pages.about_us.submenus.contact.title", { returnObjects: true });
  return (
    <div className="content">
      <CoverSection title={title} backgroundImage={globalCover} />
      <ContactForm t={t}/>
    </div>
  );
}

const Gallery = ({t}) => {
  const title = t("pages.about_us.submenus.gallery.title", { returnObjects: true });

  const imageCount = 16;
  const images = Array.from({ length: imageCount }, (_, i) => `/images/gallery/photo${i + 1}.jpg`);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const largeImageIndexes = useMemo(() => {
    return new Set(Array.from({ length: Math.floor(imageCount / 3) }, () => Math.floor(Math.random() * imageCount)));
  }, [imageCount]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex !== null) {
        if (e.key === "ArrowRight") {
          setSelectedIndex((prev) => (prev + 1) % images.length);
        } else if (e.key === "ArrowLeft") {
          setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
        } else if (e.key === "Escape") {
          setSelectedIndex(null);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, images.length]);

  return (
    <div className="content">
      <CoverSection title={title} backgroundImage={globalCover} />
      <div className="gallery-container">
        {images.map((img, index) => (
          <img key={index} src={img} alt={`Gallery ${index+1}`} className={`gallery-image ${largeImageIndexes.has(index) ? "large-image" : ""}`}  onClick={() => setSelectedIndex(index)} loading="lazy"/>
        ))}
      </div>
      {selectedIndex !== null && (
        <div className="lightbox" onClick={() => setSelectedIndex(null)}>
          <button className="lightbox-prev" onClick={(e) => { e.stopPropagation(); setSelectedIndex((selectedIndex - 1 + images.length) % images.length); }}>‹</button>
          <div className="lightbox-content">
            <img src={images[selectedIndex]} alt="Full size" className="lightbox-image" loading="lazy"/>
          </div>
          <button className="lightbox-next" onClick={(e) => { e.stopPropagation(); setSelectedIndex((selectedIndex + 1) % images.length); }}>›</button>
        </div>
      )}
    </div>
  );
}

const Maintenance = ({ t, pages }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  useEffect(() => {
    setIsOpen(true);
  }, [location]);

  return (
    <div>
      <div className="app-container">
        <Header pages={pages}/>
        <main className="main-content">
          <Home t={t} />
        </main>
        <Footer t={t} pages={pages}/>
        <LanguageSelector />
      </div>
      {isOpen && (
        <div onClick={() => setIsOpen(false)} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", backgroundColor: "rgba(0, 0, 0, 0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", textAlign: "center", width: "80%", maxWidth: "400px" }}>
            <h1>{t("pages.home.maintenance.title")}</h1>
            <p>{t("pages.home.maintenance.text")}</p>
            <button onClick={() => setIsOpen(false)} style={{ marginTop: "10px", padding: "8px 12px", background: "gray", color: "white", border: "none", borderRadius: "5px", cursor: "pointer" }}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
};

const App = () => {
  const { t } = useTranslation();
  const pages = Object.fromEntries(
    Object.entries(t("pages", { returnObjects: true }))
      .filter(([_, value]) => !value.hide)
      .map(([key, value]) => {
        if (value.submenus) {
          const visibleSubmenus = Object.fromEntries(Object.entries(value.submenus).filter(([_, submenu]) => !submenu.hide));
          return [key, { ...value, submenus: visibleSubmenus }];
        }
        return [key, value];
      })
  );
  const pageComponents = {
    Home: Home,
    Bookings: Bookings,
    Rates: Rates,
    Services: Services,
    Contact: Contact,
    Gallery: Gallery,
    History: History,
    Faq: Faq
  };

  const siteActive = JSON.parse(process.env.REACT_APP_WEBSITE_ACTIVE || "true");
  if (!siteActive) {
    return (
      <Router>
        <Routes>
          <Route path="*" element={<Maintenance t={t} pages={pages} />} />
        </Routes>
      </Router>
    );
  }

  return (
    <>
      <Router>
        <ScrollToTopOrElement />
        <div className="app-container">
          <Header pages={pages}/>
          <main className="main-content">
            <Routes>
              {Object.keys(pages).map((key) => {
                const { to, element, submenus } = pages[key];
                const Component = pageComponents[element];
                if (!submenus) {
                  return (
                    <Route key={key} path={to} element={<Component t={t} />}/>
                  );
                }
                return null;
              })}
              {Object.keys(pages).map((key) => {
                const { submenus } = pages[key];
                return (
                  submenus && Object.keys(submenus).map((subKey) => {
                    const { to: subTo, element: subElement } = submenus[subKey];
                    const SubComponent = pageComponents[subElement];
                    return <Route key={subKey} path={subTo} element={<SubComponent t={t} />} />;
                  })
                );
              })}
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </main>
          <Footer t={t} pages={pages}/>
          <LanguageSelector />
        </div>
      </Router>
    </>
  )
};

export default App;
